/**
 * Rare spawns (M36, docs/RARES.md; Kevin 2026-10-10): "rare spawns, with a unique model and animations, that sparkle and
 * glow (like shiny Pokemon), which take the place of an entire wave, have a 0.5 % chance of appearing per wave, are as
 * strong as a boss and if killed immediately give a chest … You should lose the level if you don't kill it before it
 * gets into your base … 3 seconds after it spawns, you should be given a set of cards to allow upgrades / new towers
 * 3 times in a row back to back (no roads)."
 *
 * - Roll: when a stage starts (`startStage`, or joining a running wave), on a level from `rareRules.fromLevel`,
 *   from `fromStage`, never the last (boss) stage, at most once a run: a draw from the run's seed and the stage (never the
 *   run's RNG, so a run without a rare plays — and hashes — exactly as before) under `rareChance`.
 * - M37 (Kevin 2026-10-11, "Iron and endless can have rares, we need to allow them to be farmable"):
 *   - **Iron** (one long wave of the level's stages as blocks): each block from `fromStage` to the one before the boss's
 *     rolls as a campaign stage would; the first that rolls is ONE rare in place of that block's spawns (its time slot:
 *     the blocks after it keep their ticks). At most one an Iron run. The level's chapter's rare.
 *   - **Endless**: every wave from `fromStage` but the boss waves rolls; a rare replaces the wave. More than one a run,
 *     `endlessCooldownStages` waves apart (and never while one is alive). The rare is the chapter of the run's *through*
 *     (the player's frontier, the level its power, pool and chest are rolled at), its HP from the wave's power.
 * - The stage is then ONE creature: the rare of the level's chapter, HP the stronger of the level's boss at that stage
 *   and the whole wave it replaces, × `hpMult`; a boss
 *   for targeting (`isBoss`), but no boss mechanic and no BossSpawned (RareSpawned instead). Kevin 2026-10-25 ("too difficult
 *   to defeat"): `isRare` lets stuns, roots, polymorph, pet stuns and full area damage take it, as on any raider
 *   (`controllable`, `isHeavy`); executes still spare it.
 * - Reaching the fortress costs all of its health: the level is lost (RareEscaped, then LevelLost).
 * - Killed (RareKilled): a chest `chestRarityStep` rarities above the level's battle chest for a win (capped at Mythic),
 *   rolled at once into `LevelSim.rareChest` from the seed and the stage, so a checkpoint retry that kills it again
 *   rolls the very same chest (the app banks it once, keyed by its seed).
 * - `offerDelayTicks` of running action after it appears, `offers` rally offers open back to back (each as soon as no
 *   other offer is open): tower and Upgrade cards only, no road, plus an Ascend/skill card when one fits. They follow the
 *   in-run offer rules (the action pauses while one is open) but sit outside the card pace: they neither wait for the
 *   points threshold nor move it, and taking a card from one moves no pace counter.
 */
import { BalanceData, Rarity, bossHpMultOf, stagePowerOf } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { Pcg32 } from "../maths/pcg32";
import { RARE_CHANCE_SCALE } from "../data/spawnRules";
import { battleChestBand, chestOfRarity, rollChest } from "./chest";
import { Enemy, OfferKind } from "./entities";
import { SimEventKind } from "./events";
import type { LevelSim } from "./levelSim";
import { SpawnEntry } from "./waveSchedule";
import { endlessBossWave, endlessBosses, endlessPower, endlessWave, ironWave, stageSchedule } from "./runModes";
import { buildWaveSchedule } from "./waveSchedule";
import { handSupply } from "./systems/draftFlow";
import { dealSpecialRally } from "./branches/ascension";
import { wyrmEntry } from "./wyrm";

/** What the run's rare is (null on a run without one). `alive` false once it is killed or got in. */
export interface RareRun {
  readonly enemyId: number;
  readonly archetype: number;
  /** The chapter it belongs to (1-based): the rare index of RareSpawned. */
  readonly chapter: number;
  readonly stage: number;
  /** The tick it appeared. */
  readonly startedTick: number;
  outcome: RareOutcome;
}
export type RareOutcome = "alive" | "killed" | "escaped";

const LEVELS_PER_CHAPTER = 10;
const RARE_TAG = "rareStage", RARE_CHEST_TAG = "rareChest";

/** The chapter (1-based) of campaign level `levelNumber`. */
export function chapterOfLevel(levelNumber: number): number { return Math.floor((levelNumber - 1) / LEVELS_PER_CHAPTER) + 1; }

/** The rare archetype for campaign level `levelNumber` (its chapter's; the last chapter's past the end), or −1. */
export function rareArchetypeFor(balance: BalanceData, levelNumber: number): number {
  const chapter = chapterOfLevel(levelNumber);
  let best = -1;
  for (const e of balance.enemies) if (e.rareChapter > 0 && e.rareChapter <= chapter && (best < 0 || e.rareChapter > balance.enemies[best].rareChapter)) best = e.index;
  return best;
}

/** The six (one per chapter) rare archetypes, by chapter. */
export function rareArchetypes(balance: BalanceData): number[] {
  return balance.enemies.filter((e) => e.rareChapter > 0).sort((a, b) => a.rareChapter - b.rareChapter).map((e) => e.index);
}

function stageHash(tag: string, seed: bigint, stage: number): bigint {
  const h = new Fnv1a64();
  h.writeString(tag);
  h.writeLong(seed);
  h.writeInt(stage);
  return h.value;
}

/** The campaign level whose chapter's rare a run brings and whose chest it pays: Endless's *through*, else the level. */
export function rareLevelOf(sim: LevelSim): number { return sim.mods.endless ? sim.mods.endless.through : sim.levelNumber; }

/** Iron's blocks (the level's stages, one long wave). */
const ironBlocks = (sim: LevelSim): number => sim.balance.stagesOf(sim.levelNumber);

/**
 * Whether the rules let stage `stage` of this run roll a rare: not the first stages nor the boss stage (Iron: its blocks;
 * Endless: its boss waves), none yet (Endless: none alive and the last `endlessCooldownStages` waves or more ago).
 */
export function rareEligible(sim: LevelSim, stage: number): boolean {
  const r = sim.balance.spawnRules.rare;
  // Daily Road (MP-20): no rares — every player's road is the same, and a fair run banks no chest.
  if (sim.debugSandbox || sim.mods.daily !== null || sim.mods.event !== null || sim.mods.challenge !== null || r.chancePerMillion <= 0 || stage < r.fromStage || rareArchetypeFor(sim.balance, rareLevelOf(sim)) < 0) return false;
  if (stage === sim.wyrmStage || sim.wyrm !== null || sim.wyrmChoiceOpen) return false;   // M40: the Wyrm's stage, or it is about (sim/wyrm.ts)
  if (sim.isEndless) {
    const last = sim.rareRun;
    return !endlessBossWave(sim.balance, stage) && stage < sim.stages && (last === null || (last.outcome !== "alive" && stage - last.stage >= r.endlessCooldownStages));
  }
  const last = sim.isIron ? ironBlocks(sim) : sim.stages;
  return sim.rareRun === null && sim.levelNumber >= r.fromLevel && stage < last;
}

/** The pure draw: does stage `stage` of a run with this seed bring a rare (when eligible)? */
export function rollsRare(balance: BalanceData, seed: bigint, stage: number): boolean {
  const per = balance.spawnRules.rare.chancePerMillion;
  return per > 0 && new Pcg32(stageHash(RARE_TAG, seed, stage)).nextInt(RARE_CHANCE_SCALE) < per;
}

/**
 * The rare's HP multiplier on its archetype: the stronger of the level's boss at this stage's power (boss HP mult × the
 * boss archetype's HP) and the whole wave it replaces (every spawn's HP at its power) — "as strong as a boss", and never
 * a softer stage than the one it takes the place of — × `hpMult` (Iron: also × `ironHpMult`; Endless: the boss in turn
 * at the wave's power P(w)).
 */
export function rareHpMult(sim: LevelSim, stage: number, archetype: number): Fix64 {
  const B = sim.balance, t = B.tables;
  const R = B.spawnRules.rare;
  const per = (hp: Fix64): Fix64 => { const m = hp.div(Fix64.max(B.enemies[archetype].hp, Fix64.One)).mul(R.hpMult); return sim.isIron ? m.mul(R.ironHpMult) : m; };
  const sum = (entries: readonly SpawnEntry[]): Fix64 => entries.reduce((a, e) => a.add(B.enemies[e.archetype].hp.mul(e.hpMult)), Fix64.Zero);
  if (sim.mods.endless) {
    // Endless: the wave's power (P(w) at *through*), the boss in turn at the economy's boss health, or the wave itself
    const through = sim.mods.endless.through, bosses = endlessBosses(B, through), every = Math.max(1, B.modes.endless.bossEvery);
    const power = endlessPower(B, through, stage);
    const bossHp = bosses.length > 0 ? B.enemies[bosses[(Math.ceil(stage / every) - 1) % bosses.length]].hp : B.enemies[sim.level.bossEnemyIndex].hp;
    return per(Fix64.max(power.mul(B.economy.bossHpMult).mul(bossHp), sum(endlessWave(B, through, stage, sim.seed, 0))));
  }
  let power = t.levelBaseAt(sim.levelNumber).mul(stagePowerOf(B, sim.level, stage));
  if (!sim.mods.enemyHpMult.eq(Fix64.One)) power = power.mul(sim.mods.enemyHpMult);
  const boss = power.mul(bossHpMultOf(B, sim.level)).mul(B.enemies[sim.level.bossEnemyIndex].hp);
  // Iron: the block it replaces (that stage's spawns at the Iron health; never the boss's block)
  const replaced = sim.isIron ? buildWaveSchedule(B, sim.level, sim.levelNumber, stage, ironBlocks(sim) + 1, 0, sim.mods)
    : stageSchedule(B, sim.level, sim.levelNumber, stage, sim.stages, 0, sim.mods, sim.seed);
  return per(Fix64.max(boss, sum(replaced)));
}

/**
 * The stage's spawns if it is a rare stage (forced by the dev hook, or eligible and drawn), else null. Called once per
 * stage start; a rare stage is remembered (`rareStage`) for the preview.
 */
export function rareStageSchedule(sim: LevelSim, stage: number, startDelay: number): SpawnEntry[] | null {
  if (sim.isIron) return ironRareSchedule(sim, startDelay);
  const forced = sim.forceRareNextStage && !sim.debugSandbox;
  if (!forced && !(rareEligible(sim, stage) && rollsRare(sim.balance, sim.seed, stage))) return null;
  sim.forceRareNextStage = false;
  const archetype = rareArchetypeFor(sim.balance, rareLevelOf(sim));
  if (archetype < 0) return null;
  sim.rareStage = stage;
  return [rareEntry(sim, stage, startDelay, archetype)];
}

/**
 * Iron's one wave with its rare block (M37): the first block from `fromStage` before the boss's that rolls (the dev hook:
 * the first eligible block), ONE rare in that block's place, or null (no rare: the plain Iron wave). `rareStage` is the block.
 */
function ironRareSchedule(sim: LevelSim, startDelay: number): SpawnEntry[] | null {
  const forced = sim.forceRareNextStage && !sim.debugSandbox;
  sim.forceRareNextStage = false;
  const blocks = ironBlocks(sim);
  let block = 0;
  for (let b = sim.balance.spawnRules.rare.fromStage; b < blocks && block === 0; b++) if (rareEligible(sim, b) && (forced || rollsRare(sim.balance, sim.seed, b))) block = b;
  if (block === 0 && forced && blocks > 1) block = Math.max(1, Math.min(sim.balance.spawnRules.rare.fromStage, blocks - 1));
  if (block === sim.wyrmStage) block = 0;                 // M40: that block is the Wyrm's
  if (block === 0 || rareArchetypeFor(sim.balance, sim.levelNumber) < 0) return null;
  sim.rareStage = block;
  return ironRareWave(sim, startDelay);
}

/** Iron's wave with the rare in block `sim.rareStage` and the Wyrm (M40) in block `sim.wyrmStage` (the run and the wave preview build it alike). */
export function ironRareWave(sim: LevelSim, startDelay: number): SpawnEntry[] {
  const block = sim.rareStage, wyrm = sim.wyrmStage;
  return ironWave(sim.balance, sim.level, sim.levelNumber, startDelay, sim.mods, block, block > 0 ? (tick) => rareEntry(sim, block, tick) : null,
    wyrm, wyrm > 0 ? (tick) => wyrmEntry(sim, wyrm, tick) : null);
}

/** The one spawn of a rare stage (the wave preview shows it once the stage is known to be rare). */
export function rareEntry(sim: LevelSim, stage: number, startDelay: number, archetype = rareArchetypeFor(sim.balance, rareLevelOf(sim))): SpawnEntry {
  return { tick: startDelay, archetype, hpMult: rareHpMult(sim, stage, archetype), isBoss: true, pointsOverride: sim.balance.economy.bossPoints, rare: true };
}

/** A rare came out of the portal (enemyMotion.spawn): the run's rare, the rally's clock, all the fortress's health at stake. */
export function onRareSpawned(sim: LevelSim, e: Enemy): void {
  const a = sim.balance.enemies[e.archetype];
  sim.rareRun = { enemyId: e.id, archetype: e.archetype, chapter: a.rareChapter, stage: sim.rareStage > 0 ? sim.rareStage : sim.stage, startedTick: sim.tick, outcome: "alive" };
  sim.rareRuns.push(sim.rareRun);
  e.leakDamage = Math.max(a.leakDamage, sim.maxBaseHp);
  sim.rareOffersLeft = sim.balance.spawnRules.rare.offers;
  sim.rareOfferCountdown = sim.balance.spawnRules.rare.offerDelayTicks;
  sim.events.add(SimEventKind.RareSpawned, e.id, a.rareChapter, e.archetype);
}

/** Whether `e` is the run's rare, alive. */
export function isLiveRare(sim: LevelSim, e: Enemy): boolean { return sim.rareRun !== null && sim.rareRun.outcome === "alive" && sim.rareRun.enemyId === e.id; }

/** The rare died (applyDamage): its chest is rolled and held for the app to bank at once. */
export function onRareKilled(sim: LevelSim, e: Enemy): void {
  const run = sim.rareRun!;
  run.outcome = "killed";
  // Iron and Endless pay as a farm win would (an Iron win is a farm win of its level; Endless at its *through*)
  sim.rareChest = rollRareChest(sim.balance, rareLevelOf(sim), sim.firstClear && !sim.isIron && !sim.isEndless, sim.seed, run.stage);
  sim.events.add(SimEventKind.RareKilled, e.id, sim.rareChest.rarity, e.archetype);
}

/** The rare reached the fortress (moveEnemies, before the leak's damage): it takes all that is left. */
export function onRareEscaped(sim: LevelSim, e: Enemy): void {
  sim.rareRun!.outcome = "escaped";
  e.leakDamage = Math.max(e.leakDamage, sim.baseHp);
  sim.rareOffersLeft = 0;
  sim.events.add(SimEventKind.RareEscaped, e.id, 0, e.archetype);
}

/**
 * The rare's chest: the level's battle chest for a win (its band: first clear, farm or late first clear) rolled from the
 * run's seed and the stage, then `chestRarityStep` rarities up, capped at Mythic, as a chest of that rarity.
 */
export function rollRareChest(balance: BalanceData, levelNumber: number, firstClear: boolean, seed: bigint, stage: number) {
  const seedOf = stageHash(RARE_CHEST_TAG, seed, stage);
  const base = rollChest(balance, levelNumber, battleChestBand(balance, levelNumber, true, 0, firstClear), seedOf);
  const rarity = Math.min(Rarity.Mythic, base.rarity + balance.spawnRules.rare.chestRarityStep) as Rarity;
  return chestOfRarity(balance, levelNumber, rarity, seedOf);
}

/**
 * Each running (unpaused) tick of a wave or a break: counts the rally's delay down, then opens the next rally offer as
 * soon as no offer is open. Stops when the rare is gone (escaped) or the level is over.
 */
export function tickRareOffers(sim: LevelSim): void {
  if (sim.rareOffersLeft <= 0 || sim.isOver) return;
  if (sim.rareOfferCountdown > 0) { sim.rareOfferCountdown--; return; }
  if (sim.offerOpen) return;
  sim.rareOffersLeft--;
  openRallyOffer(sim);
}

/**
 * One rally offer: tower cards (up to a full hand of different types) and the Upgrade when a tower can take a tier, no
 * road, plus an Ascend/skill card when one fits; `offer.rare` marks it. A hand with fewer than two cards does not show.
 */
function openRallyOffer(sim: LevelSim): void {
  const supply = handSupply(sim), size = sim.balance.cards.offerSize;
  const upgrade = supply.upgradeable;
  const towers = Math.max(0, Math.min(size - (upgrade ? 1 : 0), supply.towerTypes));
  sim.offer.reset(++sim.offerCounter, OfferKind.PostWave);
  sim.offer.rare = true;
  sim.offer.towersDue = towers > 0; sim.offer.upgradeDue = upgrade; sim.offer.roadDue = false;
  sim.offer.plan = { towers, upgrade, road: 0, borrowed: null, borrowedEarly: 0 };
  const drawn = sim.generator.fill(sim.offer, sim.road, sim.owned, sim.towerSlots, sim.rng, null);
  if (drawn === 0) { sim.offer.rare = false; return; }
  sim.offerOpen = true;
  dealSpecialRally(sim);
  sim.events.add(SimEventKind.OfferOpened, sim.offer.id, OfferKind.PostWave);
}
